import * as THREE from 'three';
import type { CharacterRig, CharacterSpec } from './CharacterBuilder';

// Opaque, vertex-coloured decals share one unlit material and never cast shadows.
const injuryMaterial = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide });
type Point = [number, number];
type Point3 = [number, number, number];
type Surface = (x: number, y: number) => number;
type Mark = { damage: number; positions: number[]; colors: number[] };

const HEAD = [
  [-0.195, 0.09, 0.125], [-0.165, 0.15, 0.205], [-0.115, 0.19, 0.25],
  [-0.05, 0.225, 0.262], [0.025, 0.252, 0.245], [0.105, 0.267, 0.232],
  [0.185, 0.25, 0.214], [0.255, 0.205, 0.163], [0.31, 0.118, 0.08],
];

function faceSurface(x: number, y: number): number {
  let i = 1;
  while (i < HEAD.length - 1 && HEAD[i][0] < y) i++;
  const a = HEAD[i - 1], b = HEAD[i];
  const t = THREE.MathUtils.clamp((y - a[0]) / (b[0] - a[0]), 0, 1);
  const width = THREE.MathUtils.lerp(a[1], b[1], t);
  const front = THREE.MathUtils.lerp(a[2], b[2], t);
  const cosine = Math.pow(Math.min(0.995, Math.abs(x) / width), 1 / 0.82);
  return -0.015 + Math.pow(Math.sqrt(1 - cosine * cosine), 0.48) * (front + 0.015) + 0.007;
}

// Clipped jagged outlines read as cloth tears and bruises, rather than square labels.
const ragged: Point[] = [[-0.5, -0.18], [-0.32, -0.43], [-0.05, -0.34], [0.2, -0.5],
  [0.47, -0.2], [0.36, 0.04], [0.5, 0.33], [0.19, 0.43], [-0.12, 0.31], [-0.39, 0.5], [-0.43, 0.13]];

class InjuryBatch {
  private readonly marks: Mark[] = [];

  solid(damage: number, color: THREE.ColorRepresentation, source: THREE.BufferGeometry): void {
    const geometry = source.index ? source.toNonIndexed() : source;
    const positions = Array.from(geometry.getAttribute('position').array);
    const colors: number[] = [];
    const tint = new THREE.Color(color);
    for (let i = 0; i < positions.length; i += 3) {
      // Baked directional shading keeps the swollen silhouette legible without lighting work.
      const normalY = geometry.getAttribute('normal')?.getY(i / 3) ?? 0;
      const normalX = geometry.getAttribute('normal')?.getX(i / 3) ?? 0;
      const normalZ = geometry.getAttribute('normal')?.getZ(i / 3) ?? 0;
      const shade = 0.55 + Math.max(0, normalY * 0.8 + normalZ * 0.5 - normalX * 0.3) * 0.42;
      colors.push(tint.r * shade, tint.g * shade, tint.b * shade);
    }
    this.marks.push({ damage, positions, colors });
    geometry.dispose();
    if (geometry !== source) source.dispose();
  }

  flap(damage: number, color: THREE.ColorRepresentation, points: Point3[]): void {
    const positions: number[] = [], colors: number[] = [];
    const tint = new THREE.Color(color);
    for (let i = 1; i < points.length - 1; i++) {
      for (const point of [points[0], points[i], points[i + 1]]) {
        positions.push(...point);
        colors.push(tint.r, tint.g, tint.b);
      }
    }
    this.marks.push({ damage, positions, colors });
  }

  patch(damage: number, color: THREE.ColorRepresentation, x: number, y: number,
    width: number, height: number, surface: Surface, outline: Point[] = ragged): void {
    const positions: number[] = [], colors: number[] = [];
    const tint = new THREE.Color(color);
    for (let i = 0; i < outline.length; i++) {
      const a = outline[i], b = outline[(i + 1) % outline.length];
      for (const [px, py] of [[x, y], [x + a[0] * width, y + a[1] * height], [x + b[0] * width, y + b[1] * height]]) {
        positions.push(px, py, surface(px, py));
        colors.push(tint.r, tint.g, tint.b);
      }
    }
    this.marks.push({ damage, positions, colors });
  }

  slash(damage: number, color: THREE.ColorRepresentation, x: number, y: number,
    length: number, surface: Surface): void {
    this.patch(damage, color, x, y, length, length * 0.37, surface,
      [[-0.5, -0.5], [-0.24, 0.06], [0.5, 0.5], [0.22, -0.12]]);
  }

  attach(parent: THREE.Object3D, name: string): (damage: number) => void {
    this.marks.sort((a, b) => a.damage - b.damage);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(this.marks.flatMap(mark => mark.positions), 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(this.marks.flatMap(mark => mark.colors), 3));
    geometry.computeBoundingSphere();
    geometry.setDrawRange(0, 0);
    const mesh = new THREE.Mesh(geometry, injuryMaterial);
    mesh.name = name;
    mesh.visible = false;
    parent.add(mesh);
    return damage => {
      let count = 0;
      for (const mark of this.marks) {
        if (damage < mark.damage) break;
        count += mark.positions.length / 3;
      }
      geometry.setDrawRange(0, count);
      mesh.visible = count > 0;
    };
  }
}

const starShape = new THREE.Shape();
for (let i = 0; i < 10; i++) {
  const angle = Math.PI / 2 + i * Math.PI / 5;
  const radius = i % 2 === 0 ? 0.11 : 0.046;
  const x = Math.cos(angle) * radius, y = Math.sin(angle) * radius;
  if (i === 0) starShape.moveTo(x, y);
  else starShape.lineTo(x, y);
}
starShape.closePath();
const starGeometry = new THREE.ExtrudeGeometry(starShape, { depth: 0.035, bevelEnabled: false, steps: 1 });
starGeometry.translate(0, 0, -0.0175);
const starColors = new Float32Array(starGeometry.getAttribute('position').count * 3);
const gold = new THREE.Color('#ffe12f');
for (let i = 0; i < starColors.length; i += 3) {
  starColors[i] = gold.r;
  starColors[i + 1] = gold.g;
  starColors[i + 2] = gold.b;
}
starGeometry.setAttribute('color', new THREE.BufferAttribute(starColors, 3));

/** Damage is cumulative lost health, 0 (unhurt) to 1 (knocked out). Call only when HP changes. */
export function buildInjuries(rig: CharacterRig, spec: CharacterSpec): {
  setDamage(amount: number): void;
  update(elapsed: number, unconscious: boolean): void;
} {
  const face = new InjuryBatch();
  const purple = '#66345f', bruise = '#ae597b', blood = '#a51d32', fresh = '#e04750';
  const faceRaised: Surface = (x, y) => faceSurface(x, y) + 0.002;
  face.patch(0.16, bruise, -0.112, 0.026, 0.17, 0.105, faceSurface);
  face.patch(0.34, purple, -0.107, 0.051, 0.14, 0.061, faceRaised);
  face.slash(0.34, blood, 0.155, -0.031, 0.085, faceRaised);
  face.slash(0.49, blood, -0.12, 0.193, 0.092, faceRaised);
  face.patch(0.49, blood, -0.025, -0.019, 0.032, 0.098, () => 0.319);
  face.patch(0.65, bruise, 0.108, 0.028, 0.17, 0.10, faceSurface);
  face.patch(0.65, purple, 0.111, 0.055, 0.13, 0.06, faceRaised);
  face.patch(0.8, blood, 0.055, -0.092, 0.09, 0.062, () => 0.292);
  face.slash(0.8, fresh, -0.124, 0.195, 0.065, (x, y) => faceSurface(x, y) + 0.004);

  // Punch-drunk boxer shapes break the silhouette, so damage also reads from the match camera.
  const lump = (damage: number, color: string, at: Point3, scale: Point3) => face.solid(damage, color,
    new THREE.SphereGeometry(1, 8, 5).scale(...scale).translate(...at));
  lump(0.34, '#9b459b', [-0.215, 0.001, 0.203], [0.11, 0.09, 0.085]);
  lump(0.49, '#d94e59', [0, 0.015, 0.302], [0.083, 0.071, 0.073]);
  face.solid(0.49, purple, new THREE.TorusGeometry(0.066, 0.025, 4, 10)
    .scale(1.24, 0.88, 1).translate(-0.105, 0.098, 0.232));
  lump(0.65, '#b94f91', [0.224, -0.015, 0.195], [0.117, 0.096, 0.087]);
  lump(0.8, '#d86688', [0.015, -0.126, 0.283], [0.122, 0.049, 0.052]);
  for (const side of [-1, 1]) {
    lump(0.65, '#753674', [side * 0.235, 0.045, 0.195], [0.14, 0.115, 0.105]);
    lump(0.85, '#9b487c', [side * 0.106, 0.114, 0.255], [0.091, 0.048, 0.055]);
    face.patch(0.8, '#452443', side * 0.105, 0.095, 0.155, 0.09, () => 0.273);
    face.slash(0.85, blood, side * 0.148, -0.064, 0.145, (x, y) => faceSurface(x, y) + 0.013);
    face.patch(0.85, fresh, side * 0.065, -0.137, 0.035, 0.095, () => 0.254);
    face.patch(0.85, blood, side * 0.22, 0.015, 0.105, 0.043, () => 0.309);
  }
  lump(0.85, '#98579b', [-0.16, 0.253, 0.135], [0.11, 0.115, 0.1]);
  lump(0.85, '#b1425c', [0.015, 0.015, 0.322], [0.11, 0.09, 0.105]);
  face.patch(0.85, blood, 0.02, -0.02, 0.037, 0.065, () => 0.427);

  const shirt = new InjuryBatch();
  const skin = new THREE.Color(spec.skin);
  const shadowSkin = skin.clone().multiplyScalar(0.57);
  const clothEdge = new THREE.Color(spec.jersey).lerp(new THREE.Color('#eee3bf'), 0.55);
  const front: Surface = (x, y) => {
    const depth = y > 0.4 ? 0.171 : y > 0.2 ? 0.185 : 0.168;
    return depth * Math.pow(Math.max(0.02, 1 - Math.pow(Math.abs(x) / 0.25, 2 / 0.64)), 0.32) + 0.008;
  };
  const back: Surface = (x, y) => -front(x, y);
  for (const surface of [front, back]) {
    const sign = surface === front ? 1 : -1;
    const over: Surface = (x, y) => surface(x, y) + sign * 0.001;
    shirt.patch(0.16, '#665841', 0.145, 0.18, 0.12, 0.14, surface);
    shirt.slash(0.34, '#342c2a', -0.13, 0.33, 0.13, surface);
    shirt.patch(0.49, '#342c2a', 0.11, 0.21, 0.19, 0.16, surface);
    shirt.patch(0.49, shadowSkin, 0.11, 0.21, 0.155, 0.12, over);
    shirt.patch(0.49, skin, 0.10, 0.224, 0.125, 0.075, (x, y) => over(x, y) + sign * 0.001);
    shirt.slash(0.49, clothEdge, 0.11, 0.27, 0.14, over);
    shirt.patch(0.65, blood, -0.135, 0.16, 0.14, 0.09, over);
    shirt.patch(0.8, '#342c2a', -0.12, 0.40, 0.18, 0.18, surface);
    shirt.patch(0.8, skin, -0.12, 0.40, 0.13, 0.14, over);
    shirt.slash(0.8, clothEdge, -0.14, 0.46, 0.14, (x, y) => over(x, y) + sign * 0.001);
    shirt.slash(0.8, blood, 0.112, 0.21, 0.11, (x, y) => over(x, y) + sign * 0.002);
    shirt.flap(0.49, spec.jersey, [[0.05, 0.258, sign * 0.185], [0.15, 0.25, sign * 0.18],
      [0.20, 0.16, sign * 0.30], [0.11, 0.195, sign * 0.27], [0.10, 0.13, sign * 0.29]]);
    shirt.flap(0.8, clothEdge, [[-0.21, 0.455, sign * 0.13], [-0.10, 0.47, sign * 0.17],
      [-0.11, 0.37, sign * 0.29], [-0.17, 0.41, sign * 0.28], [-0.20, 0.31, sign * 0.24]]);
  }
  for (const side of [-1, 1]) {
    shirt.flap(0.65, spec.jersey, [[side * 0.21, 0.16, 0.08], [side * 0.24, 0.19, -0.1],
      [side * 0.36, -0.025, -0.18], [side * 0.30, 0.025, -0.04], [side * 0.35, -0.08, 0.06]]);
    shirt.flap(0.8, clothEdge, [[side * 0.20, 0.10, 0.10], [side * 0.23, 0.14, 0.02],
      [side * 0.34, -0.12, 0.17], [side * 0.27, -0.015, 0.14]]);
  }

  const setters = [face.attach(rig.head, 'injuries-face'), shirt.attach(rig.torso, 'injuries-torn-shirt')];
  for (const [side, chain] of [[1, rig.armL], [-1, rig.armR]] as const) {
    const arm = new InjuryBatch();
    const surface: Surface = x => 0.067 * Math.sqrt(Math.max(0.08, 1 - (x / 0.077) ** 2)) + 0.005;
    arm.patch(side > 0 ? 0.34 : 0.49, bruise, 0, -0.055, 0.10, 0.065, surface);
    arm.slash(0.65, blood, 0, -0.09, 0.09, (x, y) => surface(x, y) + 0.002);
    arm.slash(0.8, fresh, 0.012, -0.12, 0.065, (x, y) => surface(x, y) + 0.003);
    setters.push(arm.attach(chain.lower, `injuries-forearm-${side}`));
  }
  for (const [side, chain] of [[1, rig.legL], [-1, rig.legR]] as const) {
    const knee = new InjuryBatch();
    const surface: Surface = x => 0.071 * Math.sqrt(Math.max(0.08, 1 - (x / 0.084) ** 2)) + 0.005;
    knee.patch(side > 0 ? 0.34 : 0.49, '#625541', 0, -0.07, 0.10, 0.13, surface);
    knee.patch(0.65, '#302c29', 0, -0.012, 0.112, 0.072, surface);
    knee.patch(0.65, skin, 0, -0.012, 0.083, 0.052, (x, y) => surface(x, y) + 0.001);
    knee.slash(0.8, blood, 0, -0.015, 0.075, (x, y) => surface(x, y) + 0.002);
    setters.push(knee.attach(chain.lower, `injuries-knee-${side}`));
  }

  const stars = new THREE.InstancedMesh(starGeometry, injuryMaterial, 4);
  stars.name = 'injuries-dizzy-stars';
  stars.visible = false;
  stars.frustumCulled = false;
  stars.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  rig.head.add(stars);
  const starTransform = new THREE.Object3D();
  let previous = -1;
  return {
    setDamage(amount: number): void {
      const damage = Number.isFinite(amount) ? THREE.MathUtils.clamp(amount, 0, 1) : 0;
      if (damage < 0.65) stars.visible = false;
      if (damage === previous) return;
      previous = damage;
      for (const set of setters) set(damage);
      rig.root.userData.injuryDamage = damage;
    },
    update(elapsed: number, unconscious: boolean): void {
      stars.visible = previous >= 0.65 || unconscious;
      if (!stars.visible) return;
      stars.count = previous >= 0.8 || unconscious ? 4 : 3;
      for (let i = 0; i < stars.count; i++) {
        const angle = elapsed * (unconscious ? 3.7 : 2.8) + i * Math.PI * 2 / stars.count;
        starTransform.position.set(Math.cos(angle) * 0.44, 0.53 + Math.sin(angle * 2) * 0.065, Math.sin(angle) * 0.36);
        starTransform.rotation.set(Math.sin(angle) * 0.24, -angle, Math.sin(angle * 2) * 0.4);
        const scale = unconscious ? 1.16 : 1;
        starTransform.scale.setScalar(scale);
        starTransform.updateMatrix();
        stars.setMatrixAt(i, starTransform.matrix);
      }
      stars.instanceMatrix.needsUpdate = true;
    },
  };
}
